import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ArticlePropsService from "@core-ui/ContextServices/ArticleProps";
import InlineProperty from "@ext/markdown/elements/inlineProperty/edit/components/InlineProperty";
import { getEditorContext } from "@ext/markdown/elementsUtils/editorContext/EditorContext";
import PropertyServiceProvider from "@ext/properties/components/PropertyService";
import { deleteProperty, updateProperty } from "@ext/properties/logic/changeProperty";
import combineProperties from "@ext/properties/logic/combineProperties";
import { saveArticleProps } from "@ext/properties/logic/saveArticleProperties";
import type { Property } from "@ext/properties/models";
import { NodeSelection } from "@tiptap/pm/state";
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { useCallback } from "react";

const InlinePropertyComponent = ({ node, updateAttributes, extension, editor, selected }: NodeViewProps) => {
	const bind = node.attrs.bind as string;
	const isSelected = selected && editor.state.selection instanceof NodeSelection;
	const { setArticleProperties, properties } = PropertyServiceProvider.value;
	const articleProps = ArticlePropsService.value;
	const apiUrlCreator = ApiUrlCreatorService.value;

	const onUpdate = useCallback(
		(newBind: string) => {
			updateAttributes({ bind: newBind });
		},
		[updateAttributes],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	const deleteHandler = useCallback(
		(id: string) => {
			setArticleProperties((prevProps: Property[]) => {
				const newProps = deleteProperty(id, prevProps);
				if (!newProps) return prevProps;

				if (!getEditorContext(editor).noWrite)
					void saveArticleProps(apiUrlCreator, { ...articleProps, properties: newProps });

				return combineProperties(newProps, properties);
			});
		},
		[articleProps, properties],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	const updateHandler = useCallback(
		(id: string, value?: string) => {
			setArticleProperties((prevProps: Property[]) => {
				const newProps = updateProperty(id, value, properties, prevProps);
				if (!newProps) return prevProps;

				if (!getEditorContext(editor).noWrite)
					void saveArticleProps(apiUrlCreator, { ...articleProps, properties: newProps });

				return combineProperties(newProps, properties);
			});
		},
		[articleProps, properties],
	);

	const onSubmit = useCallback(
		(id: string, value: string, isDelete?: boolean) => {
			if (isDelete) deleteHandler(id);
			else updateHandler(id, value);
		},
		[updateHandler, deleteHandler],
	);

	return (
		<NodeViewWrapper as="span" data-drag-handle draggable={true}>
			<InlineProperty
				bind={bind}
				canChangeProps={extension.options.canChangeProps}
				onChangeProperty={onSubmit}
				onUpdate={onUpdate}
				props={properties}
				scope={extension.options.scope}
				selected={isSelected}
			/>
		</NodeViewWrapper>
	);
};

export default InlinePropertyComponent;
